export const ADMIN_AREA_IDS = ["overview", "usage", "operations", "settings"] as const
export type AdminAreaId = (typeof ADMIN_AREA_IDS)[number]

export const FAIR_USE_VIEW_IDS = ["drift", "users", "leads", "costs", "plans", "settings"] as const
export type FairUseViewId = (typeof FAIR_USE_VIEW_IDS)[number]

export const USAGE_VIEW_IDS = [
  "activity",
  "adoption",
  "costs",
  "training",
  "feedback",
  "fair-use-drift",
  "fair-use-users",
  "fair-use-leads",
  "fair-use-costs",
  "fair-use-plans",
  "fair-use-settings",
] as const
export type UsageViewId = (typeof USAGE_VIEW_IDS)[number]

export const OPERATIONS_VIEW_IDS = ["health", "alerts", "tenders"] as const
export type OperationsViewId = (typeof OPERATIONS_VIEW_IDS)[number]

export type AdminDashboardRouteState = {
  area: AdminAreaId
  setting?: string
  view?: string
}

export function isAdminAreaId(value: string | null): value is AdminAreaId {
  return ADMIN_AREA_IDS.includes(value as AdminAreaId)
}

export function resolveAdminArea(value: string | null): AdminAreaId {
  return isAdminAreaId(value) ? value : "overview"
}

export function resolveUsageView(value: string | null): UsageViewId {
  return USAGE_VIEW_IDS.includes(value as UsageViewId) ? (value as UsageViewId) : "activity"
}

export function resolveOperationsView(
  value: string | null,
  hasTenderRoute: boolean
): OperationsViewId {
  if (value === "tenders" && !hasTenderRoute) return "health"
  return OPERATIONS_VIEW_IDS.includes(value as OperationsViewId)
    ? (value as OperationsViewId)
    : "health"
}

export function resolveFairUseView(value: string | null): FairUseViewId {
  return FAIR_USE_VIEW_IDS.includes(value as FairUseViewId) ? (value as FairUseViewId) : "drift"
}

export function isFairUseUsageView(view: string): boolean {
  return (
    view.startsWith("fair-use-") &&
    FAIR_USE_VIEW_IDS.includes(view.slice("fair-use-".length) as FairUseViewId)
  )
}

/** Map previously published admin links to the four-area route contract. */
export function resolveLegacyAdminTarget(
  params: URLSearchParams,
  hash: string
): AdminDashboardRouteState | null {
  const area = params.get("area")
  const setting = params.get("setting")
  if (setting === "fair-use") return { area: "usage", view: "fair-use-settings" }
  if (setting === "local-ai-leads") return { area: "usage", view: "fair-use-leads" }
  if (hash.replace(/^#/, "") === "fair-use") return { area: "usage", view: "fair-use-drift" }

  if (area === "fair-use") {
    const view = params.get("view") ?? params.get("tab")
    return { area: "usage", view: `fair-use-${resolveFairUseView(view)}` }
  }
  if (area === "configuration" || area === "access-audit") {
    return {
      area: "settings",
      setting: setting ?? (area === "access-audit" ? "admin-groups" : "libraries"),
    }
  }
  return null
}

export function updateAdminRouteParams(
  current: URLSearchParams,
  next: AdminDashboardRouteState
): URLSearchParams {
  const params = new URLSearchParams(current)
  params.set("area", next.area)
  if (next.view) params.set("view", next.view)
  else params.delete("view")
  if (next.setting) params.set("setting", next.setting)
  else params.delete("setting")
  params.delete("tab")
  return params
}

/** Each focused task is an editor boundary, including Local AI Lead drafts. */
export function isSameAdminEditor(
  current: { pathname: string; search: string },
  next: { pathname: string; search: string }
): boolean {
  if (current.pathname !== next.pathname) return false
  const currentParams = new URLSearchParams(current.search)
  const nextParams = new URLSearchParams(next.search)
  const area = currentParams.get("area")
  if (area !== nextParams.get("area")) return false
  if (area === "settings") return currentParams.get("setting") === nextParams.get("setting")
  if (area === "usage") {
    const currentView = currentParams.get("view") ?? "activity"
    const nextView = nextParams.get("view") ?? "activity"
    if (isFairUseUsageView(currentView) || isFairUseUsageView(nextView)) {
      return currentView === nextView
    }
  }
  return true
}
